<template>
    <v-dialog v-model="dialog" max-width="1500px" persistent scrollable>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div>
                        <div class="booking-dialog-title">
                            <v-icon class="mr-2" color="white" size="20">mdi-email-fast-outline</v-icon>
                            {{ title }}
                        </div>
                        <div v-if="subtitle" class="booking-dialog-subtitle mt-1">{{ subtitle }}</div>
                    </div>
                    <v-btn icon dark :disabled="sending" @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <v-card-text class="pa-6 dialog-body">
                <v-row dense>
                    <!-- Who gets it -->
                    <v-col cols="4" class="dialog-pane">
                        <div class="booking-section-header mb-2">
                            <v-checkbox :input-value="allSelected" :indeterminate="someSelected && !allSelected"
                                :disabled="sending" hide-details dense class="mt-0 pt-0 mr-1" @click="toggleAll()" />
                            SUPPLIERS ({{ selectedRows.length }} of {{ rows.length }} selected)
                        </div>
                        <div class="supplier-list">
                            <div v-for="row in rows" :key="row.key" class="supplier-list-item"
                                :class="{ 'supplier-list-item--active': row.key === active_key }"
                                @click="active_key = row.key">
                                <v-checkbox :input-value="row.selected" :disabled="sending || row.status === 'sent'"
                                    hide-details dense class="mt-0 pt-0" @click.stop @change="toggleRow(row)" />
                                <div class="supplier-list-text">
                                    <div class="supplier-list-name">{{ row.supplier.supplier_name }}</div>
                                    <div v-if="row.summary" class="supplier-list-meta">{{ row.summary }}</div>
                                    <span v-if="row.contacted === false" class="supplier-list-tag">Not contacted yet</span>
                                    <span v-if="row.status === 'sent'" class="supplier-list-tag supplier-list-tag--sent">
                                        <v-icon x-small color="#27AE60" class="mr-1">mdi-check</v-icon>Sent
                                    </span>
                                    <span v-if="row.status === 'failed'" class="supplier-list-tag supplier-list-tag--failed">
                                        {{ row.error || 'Failed to send' }}
                                    </span>

                                    <!-- Every supplier's details and the services its email is about; clicking anywhere in it shows that supplier's email -->
                                    <div class="supplier-detail">
                                        <div class="supplier-detail-header">
                                            <v-icon size="13" color="#3988AA" class="mr-1">mdi-account-outline</v-icon>
                                            SUPPLIER INFORMATION
                                        </div>
                                        <!-- Email first, then phone / mobile beside it, wrapping when too long -->
                                        <div class="supplier-detail-contacts">
                                            <span class="supplier-detail-line">
                                                <v-icon size="12" color="#7C7C7C" class="mr-1">mdi-email-outline</v-icon>
                                                {{ emailsFor(row) || 'No email address' }}
                                            </span>
                                            <span v-if="row.supplier.phone" class="supplier-detail-line">
                                                <v-icon size="12" color="#7C7C7C" class="mr-1">mdi-phone-outline</v-icon>
                                                {{ row.supplier.phone }}
                                            </span>
                                            <span v-if="row.supplier.mobile" class="supplier-detail-line">
                                                <v-icon size="12" color="#7C7C7C" class="mr-1">mdi-cellphone</v-icon>
                                                {{ row.supplier.mobile }}
                                            </span>
                                        </div>
                                        <template v-if="row.services">
                                            <div class="supplier-detail-header">
                                                <v-icon size="13" color="#3988AA" class="mr-1">mdi-calendar-check-outline</v-icon>
                                                SELECTED BOOKINGS
                                            </div>
                                            <div v-for="service in row.services" :key="service.id" class="service-item"
                                                :class="{ 'service-item--off': !service.selected }">
                                                <v-checkbox :input-value="service.selected"
                                                    :disabled="sending || row.status === 'sent'" hide-details dense
                                                    class="mt-0 pt-0" @change="toggleService(row, service)" />
                                                <div class="service-item-text">
                                                    <div class="service-item-name">{{ service.name }}</div>
                                                    <div class="service-item-meta">
                                                        <span v-if="service.date">
                                                            <v-icon size="11" color="#9E9E9E">mdi-calendar-outline</v-icon>
                                                            {{ service.date }}
                                                        </span>
                                                        <span v-if="service.time">
                                                            <v-icon size="11" color="#9E9E9E">mdi-clock-outline</v-icon>
                                                            {{ service.time }}
                                                        </span>
                                                        <span v-if="service.rate" title="Final rate">
                                                            <v-icon size="11" color="#9E9E9E">mdi-cash</v-icon>
                                                            {{ service.rate }}
                                                        </span>
                                                        <span v-if="service.groupsize" title="Groupsize">
                                                            <v-icon size="11" color="#9E9E9E">mdi-account-group-outline</v-icon>
                                                            {{ service.groupsize }}
                                                        </span>
                                                    </div>
                                                    <!-- A hotel stay: a line per night, as on the Suppliers tab -->
                                                    <div v-for="(night, index) in service.nights || []" :key="index"
                                                        class="service-item-meta">
                                                        <span>
                                                            <v-icon size="11" color="#9E9E9E">mdi-calendar-outline</v-icon>
                                                            {{ night.date }}
                                                        </span>
                                                        <span v-if="night.rate" title="Final rate">
                                                            <v-icon size="11" color="#9E9E9E">mdi-cash</v-icon>
                                                            {{ night.rate }}
                                                        </span>
                                                        <span v-if="night.groupsize" title="Groupsize">
                                                            <v-icon size="11" color="#9E9E9E">mdi-account-group-outline</v-icon>
                                                            {{ night.groupsize }}
                                                        </span>
                                                    </div>
                                                    <span v-if="service.tag" class="supplier-list-tag">{{ service.tag }}</span>
                                                    <span v-if="sentFor(row, service).length" class="supplier-list-tag supplier-list-tag--sent"
                                                        :title="sentFor(row, service).map(entry => `${entry.subject} · ${$date(entry.created_at).format('DD MMM YYYY h:mma')}`).join('\n')">
                                                        <v-icon x-small color="#27AE60" class="mr-1">mdi-email-check-outline</v-icon>
                                                        Sent{{ sentFor(row, service).length > 1 ? ` ${sentFor(row, service).length}×` : '' }}:
                                                        {{ sentFor(row, service)[0].subject }}, {{ $date(sentFor(row, service)[0].created_at).format('DD MMM') }}
                                                    </span>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="note" class="dialog-note mt-3" v-html="note"></div>
                    </v-col>

                    <!-- The selected supplier's email -->
                    <v-col cols="8" v-if="active" class="dialog-pane">
                        <v-row dense>
                            <v-col cols="6">
                                <div class="booking-section-header mb-1">
                                    <v-icon size="14" color="#3988AA" class="mr-1">mdi-at</v-icon>
                                    RECIPIENT
                                    <span v-if="isInvalid(active)" class="booking-invalid ml-1">This email is invalid</span>
                                </div>
                                <v-select v-model="active.recipient" :items="active.emails" outlined dense
                                    :disabled="!!active.override.trim()" placeholder="No email on file" hide-details />
                            </v-col>
                            <v-col cols="6">
                                <div class="booking-section-header mb-1">
                                    <v-icon size="14" color="#3988AA" class="mr-1">mdi-pencil-outline</v-icon>
                                    OVERRIDE EMAIL
                                    <span class="booking-optional ml-1">(optional)</span>
                                </div>
                                <v-text-field v-model="active.override" outlined dense clearable hide-details
                                    placeholder="Send to a different address" @click:clear="active.override = ''" />
                            </v-col>
                        </v-row>
                        <div class="booking-section-header mb-1 mt-4">
                            <v-icon size="14" color="#3988AA" class="mr-1">mdi-format-title</v-icon>
                            SUBJECT
                        </div>
                        <v-text-field v-model="active.subject" outlined dense hide-details placeholder="Enter email subject" />
                        <div class="booking-section-header mb-2 mt-4">
                            <v-icon size="14" color="#3988AA" class="mr-1">mdi-text</v-icon>
                            EMAIL CONTENT
                            <span class="booking-optional ml-1">(edits apply to {{ active.supplier.supplier_name }} only)</span>
                        </div>
                        <div v-if="active.stale" class="stale-note mb-2">
                            <v-icon small color="#B26A00" class="mr-1">mdi-alert-outline</v-icon>
                            <span>The ticked services changed after this email was edited, so its service details weren't updated.</span>
                            <v-spacer />
                            <v-btn x-small text color="#B26A00" title="Rebuilds the email for the ticked services. Your edits are lost."
                                @click="rebuild(active)">Rebuild email</v-btn>
                        </div>
                        <wysiwyg :key="`${active.key}-${active.version}`" v-model="active.body" placeholder="Compose the supplier email..." />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <span v-if="sending" class="dialog-progress">Sending {{ progress.current }} of {{ progress.total }}…</span>
                <span v-else class="services-locked-note"><v-icon small color="#888" class="mr-1">mdi-lock-outline</v-icon>Service details (rates, groupsizes, dates, notes) can't be edited here. Change them on the Suppliers screen.</span>
                <v-spacer />
                <v-btn text :disabled="sending" @click="closeDialog()">{{ anySent ? 'Close' : 'Not Now' }}</v-btn>
                <v-btn color="#3988AA" :dark="sendable.length > 0 && !sending" :disabled="sendable.length === 0"
                    :loading="sending" @click="sendSelected()">
                    <v-icon small left>mdi-send</v-icon>
                    Send to {{ sendable.length }} supplier{{ sendable.length === 1 ? '' : 's' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import { loadSentServices } from "@/utils/supplierTemplateEmails"

// Every supplier email (docs/reference/SUPPLIER_CHANGE_EMAILS.md): one email per supplier,
// each previewed and editable, sent only to the suppliers left ticked. Opened by Email Supplier/s
// (a template or a booking change), Send Final Groupsizes, and the prompt
// after a change that affects booked suppliers. The sidebar lists each supplier's services:
// unticking one rebuilds that supplier's email without it. Each email goes through
// send_supplier_email, so it shows in that supplier's Email History.
export default {
    props: {
        show: { type: Boolean, default: false },
        title: { type: String, default: 'Email Suppliers' },
        subtitle: { type: String, default: '' },
        // date_change | booking_change | cancellation | groupsize: tells the API which booking
        // statuses to update (see EventController::sendSupplierEmail). An item's own
        // email_type wins, for a mix of cancellations and booking changes. None (a template)
        // keeps the old subject rule.
        email_type: { type: String, default: null },
        // [{ key, supplier, selected, contacted, summary, email_type, ...email }]
        //   email: either { services, compose } or a fixed { subject, body, build_ids }
        //   services: [{ id, build_id, name, nights, date, time, rate, groupsize, tag, selected }]
        //     nights: a hotel stay of 2+ nights, [{ date, rate, groupsize }] shown a line each
        //     build_id: matched against the event's Email History to show "Sent"
        //   compose(ticked service ids, sentTimes(build id) -> [ms]) -> { subject, body, build_ids }
        items: { type: Array, default: () => [] },
        event: { type: Object, default: () => ({}) },
        // Shown under the supplier list (HTML).
        note: { type: String, default: '' },
        // The event's sent services (loadSentServices), already loaded by the page so the "Sent"
        // tags show at once; the page reloads them on `sent`. Null: the dialog loads its own.
        sent_services: { type: Array, default: null },
    },
    data: () => ({
        rows: [],
        // Loaded here when the page passes no sent_services.
        own_sent_services: [],
        active_key: null,
        sending: false,
        progress: { current: 0, total: 0 },
    }),
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        },
        active() {
            return this.rows.find(row => row.key === this.active_key) || null
        },
        selectedRows() {
            return this.rows.filter(row => row.selected)
        },
        sendable() {
            // A row with services needs at least one ticked.
            return this.selectedRows.filter(row => row.status !== 'sent' && (!row.services || this.tickedIds(row).length > 0))
        },
        allSelected() {
            return this.rows.length > 0 && this.rows.every(row => row.selected)
        },
        someSelected() {
            return this.rows.some(row => row.selected)
        },
        // "supplier|build" → history entries (newest first) about that service
        // (SupplierService::getSentServices).
        sentIndex() {
            const index = {}
            const entries = this.sent_services || this.own_sent_services
            entries.forEach(entry => {
                new Set((entry.build_ids || []).map(Number)).forEach(id => {
                    const key = `${entry.supplier_id}|${id}`
                    if (!index[key]) index[key] = []
                    index[key].push(entry)
                })
            })
            return index
        },
        anySent() {
            return this.rows.some(row => row.status === 'sent')
        },
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.reset()
                    if (!this.sent_services) this.loadHistory()
                }
            }
        },
        // The groupsize email's Old sizes go back to the last email about each service, so
        // refresh emails not yet edited or sent once the history arrives.
        sentIndex() {
            this.rows.forEach(row => {
                if (!row.services || row.status || !row.generated) return
                if (row.subject !== row.generated.subject || row.body !== row.generated.body) return
                const fresh = row.compose(this.tickedIds(row), this.sentTimes(row))
                if (fresh.body !== row.body) this.rebuild(row)
            })
        },
    },
    methods: {
        // Which services the listed suppliers were already emailed about (no email bodies).
        async loadHistory() {
            this.own_sent_services = []
            const supplier_ids = this.rows.filter(row => row.services).map(row => row.supplier.id)
            if (!this.event || !this.event.id) return
            this.own_sent_services = await loadSentServices(this.get_selected_company.id, this.event.id, supplier_ids)
        },
        // History entries (newest first) that emailed this supplier about this service.
        sentFor(row, service) {
            if (!service.build_id) return []
            return this.sentIndex[`${row.supplier.id}|${service.build_id}`] || []
        },
        // When this supplier was emailed about a service, for compose(): build id -> [ms].
        sentTimes(row) {
            return buildId => this.sentFor(row, { build_id: buildId }).map(entry => Date.parse(entry.created_at)).filter(Boolean)
        },
        reset() {
            this.sending = false
            this.rows = this.items.map(item => {
                const emails = String(item.supplier.email || '')
                    .split(/[,;]/)
                    .map(email => email.trim())
                    .filter(email => email.length > 0)
                const services = item.services ? item.services.map(service => ({ ...service })) : null
                const row = {
                    ...item,
                    services,
                    selected: item.selected !== false,
                    emails,
                    recipient: emails[0] || null,
                    override: '',
                    status: null,
                    error: null,
                    // What compose() last produced: an email still equal to it hasn't been edited.
                    generated: null,
                    stale: false,
                    version: 0,
                }
                if (services) Object.assign(row, this.composed(row))
                return row
            })
            const first = this.rows.find(row => row.selected) || this.rows[0]
            this.active_key = first ? first.key : null
        },
        // The ticked service ids, or null when the row has a fixed email.
        tickedIds(row) {
            return row.services ? row.services.filter(service => service.selected).map(service => service.id) : null
        },
        composed(row) {
            const { subject, body, build_ids } = row.compose(this.tickedIds(row), this.sentTimes(row))
            return { subject, body, build_ids, generated: { subject, body }, stale: false }
        },
        rebuild(row) {
            Object.assign(row, this.composed(row))
            row.version++
        },
        // The override address when there is one, otherwise every address on file.
        emailsFor(row) {
            return (row.override || '').trim() || row.emails.join(', ')
        },
        // Unticking a service rebuilds the email without it. An email staff already edited is
        // left alone (only its build ids follow the ticks) and flagged, so edits aren't lost.
        toggleService(row, service) {
            service.selected = !service.selected
            const ids = this.tickedIds(row)
            if (ids.length === 0) {
                row.selected = false
            } else if (!row.selected) {
                row.selected = true
            }
            if (ids.length === 0) return
            const edited = row.generated && (row.subject !== row.generated.subject || row.body !== row.generated.body)
            if (edited) {
                const fresh = row.compose(ids, this.sentTimes(row))
                row.build_ids = fresh.build_ids
                // A blank email doesn't list services, so there's nothing out of date.
                row.stale = fresh.subject !== row.generated.subject || fresh.body !== row.generated.body
            } else {
                this.rebuild(row)
            }
        },
        // Ticking a supplier with none of its services ticked ticks them all.
        setRowSelected(row, selected) {
            row.selected = selected
            if (selected && row.services && this.tickedIds(row).length === 0) {
                row.services.forEach(service => { service.selected = true })
                this.rebuild(row)
            }
        },
        toggleRow(row) {
            this.setRowSelected(row, !row.selected)
        },
        recipientFor(row) {
            return (row.override || '').trim() || row.recipient
        },
        isInvalid(row) {
            return !(row.override || '').trim() && !!row.recipient && row.recipient.includes('freedomltd.invalid')
        },
        toggleAll() {
            const select = !this.allSelected
            this.rows.forEach(row => {
                if (row.status !== 'sent') this.setRowSelected(row, select)
            })
        },
        closeDialog() {
            if (this.sending) return
            this.$emit('close')
        },
        async sendSelected() {
            const rows = this.sendable
            const problem = rows.find(row => !this.recipientFor(row) || this.isInvalid(row) || !row.subject || !row.body)
            if (problem) {
                this.active_key = problem.key
                this.$toast.error(!this.recipientFor(problem) || this.isInvalid(problem)
                    ? `Enter a valid email address for ${problem.supplier.supplier_name}`
                    : `Fill out the subject and email content for ${problem.supplier.supplier_name}`)
                return
            }

            this.sending = true
            this.progress = { current: 0, total: rows.length }
            for (const row of rows) {
                this.progress.current++
                try {
                    const { data } = await this.$axios.post('admin/events/send_supplier_email', {
                        company: this.get_selected_company,
                        event: this.event,
                        builds: (row.build_ids || []).map(id => ({ id })),
                        supplier: row.supplier,
                        subject: row.subject,
                        body: row.body,
                        recipient_email: this.recipientFor(row),
                        email_type: row.email_type || this.email_type,
                    })
                    row.status = data.response ? 'sent' : 'failed'
                    row.error = data.response ? null : (data.message || 'Failed to send')
                } catch (error) {
                    row.status = 'failed'
                    row.error = error.response?.data?.message || 'Failed to send'
                }
                if (row.status === 'sent') row.selected = false
            }
            this.sending = false

            const sent = rows.filter(row => row.status === 'sent').length
            this.$emit('sent', sent)
            // Failed ones stay open: show what just went out as Sent.
            if (sent && !this.sent_services) this.loadHistory()
            if (sent === rows.length) {
                this.$toast.success(`Sent ${sent} supplier email${sent === 1 ? '' : 's'}`)
                this.$emit('close')
            } else {
                // Stay open so the failed ones can be fixed and sent again.
                this.$toast.warning(`Sent ${sent} of ${rows.length}. Check the suppliers marked as failed.`, { timeout: 7000 })
                const failed = rows.find(row => row.status === 'failed')
                if (failed) this.active_key = failed.key
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.services-locked-note {
    font-size: 12px;
    color: #888;
}

.booking-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
}

.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-dialog-subtitle {
    font-family: Manrope;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}

.booking-optional {
    font-weight: 400;
    color: #9E9E9E;
}

.booking-invalid {
    font-weight: 600;
    color: #E53935;
}

/* The supplier sidebar and the email scroll on their own; the dialog body doesn't. The
   panes fill the dialog's 90vh less the header, footer and padding. */
.dialog-body {
    overflow: hidden !important;
}

.dialog-pane {
    max-height: calc(90vh - 210px);
    overflow-y: auto;
}

.supplier-list {
    border: 1px solid #E9E9E9;
    border-radius: 8px;
}

.supplier-list-item {
    display: flex;
    align-items: flex-start;
    padding: 10px 12px;
    border-bottom: 1px solid #F0F0F0;
    cursor: pointer;
    font-family: Manrope;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #F7FBFD;
    }
}

.supplier-list-item--active {
    background: #EAF4F8 !important;
    box-shadow: inset 3px 0 0 #3988AA;
}

.supplier-list-text {
    min-width: 0;
    flex: 1;
}

.supplier-detail {
    margin-top: 8px;
}

.supplier-detail-header {
    display: flex;
    align-items: center;
    margin: 8px 0 4px;
    font-size: 11px;
    font-weight: 700;
    color: #3D3D3D;
    letter-spacing: 0.04em;
}

.supplier-detail-contacts {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
}

.supplier-detail-line {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: #555;
    word-break: break-all;
}

.service-item {
    display: flex;
    align-items: flex-start;
    background: white;
    border: 1px solid #E9E9E9;
    border-radius: 6px;
    padding: 6px 8px;
    margin-bottom: 4px;
}

.service-item--off {
    opacity: 0.55;
}

.service-item-text {
    min-width: 0;
}

.service-item-name {
    font-size: 12px;
    font-weight: 700;
    color: #101010;
}

.service-item-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
    font-size: 11px;
    color: #7C7C7C;

    span {
        display: inline-flex;
        align-items: center;
        gap: 2px;
    }
}

.stale-note {
    display: flex;
    align-items: center;
    font-family: Manrope;
    font-size: 12px;
    color: #B26A00;
    background: #FFF3E2;
    border-radius: 6px;
    padding: 6px 10px;
}

.supplier-list-name {
    font-size: 14px;
    font-weight: 700;
    color: #101010;
}

.supplier-list-meta {
    font-size: 12px;
    color: #7C7C7C;
    word-break: break-all;
}

.supplier-list-tag {
    display: inline-flex;
    align-items: center;
    margin-top: 4px;
    margin-right: 4px;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: #FFF3E2;
    color: #B26A00;
}

.supplier-list-tag--sent {
    background: #E6F3E2;
    color: #27AE60;
}

.supplier-list-tag--failed {
    background: #FFCFCF;
    color: #AA3939;
}

.dialog-note {
    font-family: Manrope;
    font-size: 12px;
    color: #555;
    background: #F7F7F7;
    border-radius: 6px;
    padding: 8px 10px;
}

.dialog-progress {
    font-family: Manrope;
    font-size: 13px;
    color: #555;
}
</style>
